import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, reactive, watchEffect } from 'vue';
import { formatCampaignsResponse } from '../data/fetchCampaigns';
import { getCampaignsWithFilters } from '../services/getCampaignsWithFilters';
const DEFAULT_LIMIT = 10;
const DEFAULT_ORDER_BY = '-created_at';
const STALE_TIME = 1000 * 60 * 60;
export function useCampaignsTable() {
    const queryClient = useQueryClient();
    const state = reactive({
        columns: [],
        rows: [],
        pagination: { page: 1, limit: DEFAULT_LIMIT, count: 0 },
        sort: { orderBy: DEFAULT_ORDER_BY },
        filters: {},
    });
    const hasActiveFilters = computed(() => Object.values(state.filters || {}).some(v => v !== '' && v !== undefined && v !== null));
    const queryKey = computed(() => [
        'campaigns-email-list',
        {
            page: state.pagination.page,
            limit: state.pagination.limit,
            orderBy: state.sort.orderBy,
            filters: state.filters,
        },
    ]);
    async function createFetchCampaignsQuery(page) {
        const { data } = await getCampaignsWithFilters({
            page,
            limit: state.pagination.limit,
            filters: state.filters,
            sort: state.sort.orderBy,
        });
        return formatCampaignsResponse(data);
    }
    const { data, isPending, isFetching, error } = useQuery({
        queryKey,
        queryFn: () => createFetchCampaignsQuery(state.pagination.page),
        staleTime: STALE_TIME,
        placeholderData: keepPreviousData,
    });
    const loading = computed(() => isPending.value || isFetching.value);
    watchEffect(() => {
        const result = data.value;
        if (!result)
            return null;
        state.columns = result.columns;
        state.rows = result.data;
        state.pagination.count = result.totalCount;
        return null;
    });
    function setPage(page, limit) {
        state.pagination.page = page;
        if (limit) {
            state.pagination.limit = limit;
        }
    }
    function setSort(orderBy) {
        state.sort.orderBy = orderBy;
        state.pagination.page = 1;
    }
    function setFilters(filters) {
        state.filters = { ...(filters || {}) };
        state.pagination.page = 1;
    }
    function resetFilters() {
        state.filters = {};
        state.pagination.page = 1;
    }
    function reload() {
        queryClient.invalidateQueries({ queryKey: ['campaigns-email-list'] });
    }
    return { loading, error, state, hasActiveFilters, setPage, setSort, setFilters, resetFilters, reload };
}
